import { handleActionError, reportUnexpectedError } from "@/lib/errors";
import { useInitiatePaymentSession } from "@/queries/payment";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { useCallback, useRef, useState } from "react";

import type { CartPaymentSession } from "../payment/gateway";

// The per-cart payment plan now lives with the payment engine; re-exported so
// existing `@/pages/checkout/hooks` consumers keep resolving it.
export type { CartPaymentSession } from "../payment/gateway";

export interface UsePaymentSessionsReturn {
  sessions: CartPaymentSession[];
  allReady: boolean;
  isInitializing: boolean;
  initError: string | null;
  initializeSessions: (
    carts: SalonStockerHttpTypes.StoreSellerCart[],
    providerId: string
  ) => Promise<void>;
  updateSessionStatus: (
    cartId: string,
    status: CartPaymentSession["status"],
    error?: string
  ) => void;
  reset: () => void;
}

/**
 * How long one `initiatePaymentSession` may take before the run gives up on it.
 *
 * This exists because of the radio lock, not in spite of it. While a run is in
 * flight the provider radios are disabled, so a request that *hangs* rather than
 * rejects would leave the buyer with a spinner, no error, and - unlike before
 * the lock - no way to start a second run by switching provider. Expiring into
 * the same path as a rejection restores that escape: an error they can read and
 * an unlocked step they can retry from.
 *
 * Generous on purpose. Nothing here is racing a payment; it is a backstop for a
 * request that is never coming back.
 *
 * **Per request, not per run.** Carts are initiated sequentially, so the lock it
 * bounds is worst-case `carts.length * INITIATE_TIMEOUT_MS` - and multi-seller
 * checkouts are the normal case here, not an edge one. Size it with that in
 * mind, or bound the run as a whole instead.
 */
const INITIATE_TIMEOUT_MS = 30_000;

/**
 * A payment initiation outlived {@link INITIATE_TIMEOUT_MS}. Its message is
 * written for the buyer, so it is shown as-is rather than replaced.
 */
class PaymentSetupTimeoutError extends Error {}

/**
 * Reject if `work` has not settled within `ms`. The timer is cleared either way,
 * so a slow-but-successful request does not leave one pending behind it.
 */
function withTimeout<T>(work: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    work.finally(() => clearTimeout(timer)),
    new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () =>
          reject(
            new PaymentSetupTimeoutError(
              "Setting up payment took too long. Please try again, or choose " +
                "another payment method."
            )
          ),
        ms
      );
    })
  ]);
}

/**
 * Manages payment sessions for multiple carts.
 *
 * Initiates a payment session for each cart with the selected provider, extracts
 * the provider-specific details the UI needs off the session (`client_secret`
 * for Stripe, the payee details for bank transfer), and tracks the lifecycle of
 * each payment.
 */
export function usePaymentSessions(): UsePaymentSessionsReturn {
  const [sessions, setSessions] = useState<CartPaymentSession[]>([]);
  const [isInitializing, setIsInitializing] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const initiatePayment = useInitiatePaymentSession();
  const initializedRef = useRef<string | null>(null);
  const isInitializingRef = useRef(false);
  const initiatePaymentRef = useRef(initiatePayment);
  initiatePaymentRef.current = initiatePayment;
  /**
   * Monotonic id of the initialisation run that currently owns this hook's
   * state. Bumped by every `initializeSessions` and by `reset`.
   *
   * `reset` clears `isInitializingRef` so the next provider can start, which
   * means a run whose `for` loop is still awaiting is not cancelled - it just
   * stops being the run anyone is waiting on. Without a token, whichever run
   * happened to resolve *last* won `setSessions`, so a superseded run could
   * leave the step holding sessions initiated against the provider the buyer
   * had already moved off. Each run therefore checks it is still the current
   * one before touching state, and a superseded one returns silently.
   *
   * A token cannot unsend a request, so this bounds what the *client* believes,
   * not what the server ends up holding. `CONTEXT.md` -> "Provider-session
   * pairing" is the canonical account of that limit.
   */
  const runIdRef = useRef(0);

  const allReady =
    sessions.length > 0 && sessions.every((s) => s.status === "ready");

  const initializeSessions = useCallback(
    async (
      carts: SalonStockerHttpTypes.StoreSellerCart[],
      providerId: string
    ) => {
      // Prevent re-init for the same provider
      if (initializedRef.current === providerId || isInitializingRef.current)
        return;
      initializedRef.current = providerId;
      isInitializingRef.current = true;
      const runId = ++runIdRef.current;
      /** Whether this run is still the one that owns the hook's state. */
      const owns = () => runIdRef.current === runId;
      setIsInitializing(true);
      setInitError(null);
      setSessions([]);

      try {
        // Initiate payment sessions sequentially to avoid race conditions
        const newSessions: CartPaymentSession[] = [];

        for (const cart of carts) {
          const result = await withTimeout(
            initiatePaymentRef.current.mutateAsync({
              cartId: cart.cart_id,
              providerId
            }),
            INITIATE_TIMEOUT_MS
          );

          // Superseded while awaiting: another provider now owns the state.
          // Abandon the rest of the loop rather than initiating sessions
          // nobody will read.
          if (!owns()) return;

          newSessions.push({
            cartId: cart.cart_id,
            sellerName: cart.seller.name,
            paymentSessionId: result.paymentSessionId ?? undefined,
            clientSecret: result.clientSecret ?? undefined,
            bankTransfer: result.bankTransfer ?? undefined,
            providerId,
            status: "ready"
          });
        }

        // Guarded even though the loop's last `owns()` is synchronously
        // adjacent to it. The adjacency is the kind of thing an added `await`
        // silently breaks, and "every write is behind `owns()`" is a rule
        // somebody can follow - "every write except the one that happens to
        // need no check" is not.
        if (!owns()) return;
        setSessions(newSessions);
      } catch (error) {
        // A superseded run's failure is not this checkout's failure: reporting
        // it would show the buyer an error about a provider they have already
        // moved off, and clearing `initializedRef` would re-arm the effect for
        // the provider that is currently initialising.
        if (!owns()) return;
        const boundary = "checkout payment init";
        if (error instanceof PaymentSetupTimeoutError) {
          // Shown as written for the buyer, but a hung initiation is still a
          // fault worth knowing about.
          reportUnexpectedError(error, boundary);
          setInitError(error.message);
        } else {
          setInitError(
            handleActionError(error, {
              boundary,
              fallback: "We couldn't start the payment. Please try again."
            })
          );
        }
        initializedRef.current = null; // Allow retry
      } finally {
        if (owns()) {
          isInitializingRef.current = false;
          setIsInitializing(false);
        } else if (!isInitializingRef.current) {
          // Superseded, and nothing started in our place - a `reset` with no
          // follow-up run. Nobody else is going to lower the spinner, so this
          // run does it on its way out. When a replacement run *has* started
          // (`isInitializingRef` is back up) the flag is its to lower, and
          // dropping it here would unlock the radios mid-initialisation.
          setIsInitializing(false);
        }
      }
    },
    []
  );

  const updateSessionStatus = useCallback(
    (cartId: string, status: CartPaymentSession["status"], error?: string) => {
      setSessions((prev) =>
        prev.map((s) => (s.cartId === cartId ? { ...s, status, error } : s))
      );
    },
    []
  );

  const reset = useCallback(() => {
    setSessions([]);
    setInitError(null);
    initializedRef.current = null;
    isInitializingRef.current = false;
    // Retire any in-flight run so its results land nowhere.
    //
    // `isInitializing` is deliberately *not* lowered here, so that the flag
    // means exactly one thing: a run is in flight. `reset` is called from the
    // provider radio's change handler and the effect that starts the
    // replacement run is passive, so lowering it here would commit a frame
    // with the radios enabled and the spinner hidden while the retired run is
    // still going. Today the disabled radios make that frame unreachable - you
    // cannot switch provider while locked, so `reset` never fires with a run in
    // flight - but that is the lock defending the invariant with the flag the
    // lock is derived from. Keeping the flag honest here means the guarantee
    // does not depend on that circle. The retired run's `finally` lowers it
    // instead, and only if nothing has started in its place.
    runIdRef.current += 1;
  }, []);

  return {
    sessions,
    allReady,
    isInitializing,
    initError,
    initializeSessions,
    updateSessionStatus,
    reset
  };
}
